import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { getAssetPath } from '../../utils/assetHelper';
import './LoginPage.scss';

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithTwitter, loginWithGithub, loginWithMetamask, loginWithEmail, registerWithEmail, resetPassword } = useAuth();
  const { fetchUserData, fetchCharacters, isLoading, error } = useUser();
  
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showLottie, setShowLottie] = useState(true);
  const lottieRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Set document title when component mounts
    document.title = 'Login - Phantom Clash';

    // Cleanup when component unmounts
    return () => {
      document.title = 'Phantom Clash';
    };
  }, []);

  // Add new effect to handle Lottie animation lifecycle
  useEffect(() => {
    if (showSuccess && showLottie) {
      // Get reference to the lottie player after it's rendered
      const lottieElement = document.querySelector('lottie-player') as HTMLElement;
      if (lottieElement) {
        lottieRef.current = lottieElement;
        
        // Add event listener for when animation completes
        lottieElement.addEventListener('complete', () => {
          setShowLottie(false);
        });
        
        // Safety timeout - if animation doesn't trigger complete event
        // or is causing performance issues, switch to static image after 3 seconds
        const timeout = setTimeout(() => {
          setShowLottie(false);
        }, 3000);
        
        return () => {
          clearTimeout(timeout);
          lottieElement.removeEventListener('complete', () => {
            setShowLottie(false);
          });
        };
      }
    }
  }, [showSuccess, showLottie]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setFormSubmitting(true);
    
    try {
      if (activeTab === 'register') {
        if (formData.password !== formData.confirmPassword) {
          setLoginError('Passwords do not match');
          return;
        }
        
        // Register new user
        await registerWithEmail(formData.email, formData.password);
      } else {
        // Login existing user
        await loginWithEmail(formData.email, formData.password);
      }
      
      // After successful login/register, fetch user data
      await handleSuccessfulLogin();
      
    } catch (err) {
      console.error('Authentication error:', err);
      setLoginError(activeTab === 'register' 
        ? 'Registration failed. Please try again.' 
        : 'Invalid email or password. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleSuccessfulLogin = async () => {
    setLoginError(null);
    try {
      // Fetch user data and characters
      await fetchUserData();
      await fetchCharacters();
      
      // Show success message
      setShowSuccess(true);
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      setLoginError('Failed to load user data. Please try again.');
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'twitter' | 'github' | 'metamask') => {
    setLoginError(null);
    setFormSubmitting(true);
    
    try {
      switch (provider) {
        case 'google':
          await loginWithGoogle();
          break;
        case 'twitter':
          await loginWithTwitter();
          break;
        case 'github':
          await loginWithGithub();
          break;
        case 'metamask':
          await loginWithMetamask();
          break;
      }
      
      // After successful login, fetch user data
      await handleSuccessfulLogin();
      
    } catch (error) {
      console.error(`${provider} login error:`, error);
      setLoginError(`Failed to login with ${provider}. Please try again.`);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setFormSubmitting(true);
    
    try {
      await resetPassword(forgotPasswordEmail);
      setResetSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setLoginError('Failed to send password reset email. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="c-user-login">
      <div className="c-user-login__clouds">
        <video src={getAssetPath('assets/images/hero-clouds.mp4')} autoPlay loop muted playsInline></video>
      </div>
      <div className="c-user-login__container">
        <div className="c-user-login__header">
          <img src={getAssetPath('assets/images/shadow-logo.png')} className="c-user-login__header-logo" alt="Phantom Clash" />
        </div>
        <div className="c-user-login__content">
          {showSuccess ? (
            <div className="c-user-login__success">
              <div className="lottie-player">
                  <lottie-player
                    src="https://lottie.host/e5244bb5-a5e5-4fe2-8612-22b9606907ec/OgMABUsilq.json"
                    background="transparent"
                    speed="1"
                    style={{ 
                      width: "200px", 
                      height: "200px",
                      margin: "0 auto",
                      display: "block"
                    }}
                    direction="1"
                    mode="normal"
                    autoplay
                  ></lottie-player>
              </div>
              <h4>Successfully {activeTab === 'login' ? 'logged in' : 'registered'}</h4>
              <p>Welcome to Phantom Clash</p>
              <button className="c-button" onClick={() => navigate('/')}>
                <span>Continue to Dashboard</span>
              </button>
            </div>
          ) : (
            <>
              <div className="c-user-login__navbar">
                <ul>
                  <li className={activeTab === 'login' ? 'selected' : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('login'); }}>
                      Sign In
                    </a>
                  </li>
                  <li className={activeTab === 'register' ? 'selected' : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('register'); }}>
                      Sign Up
                    </a>
                  </li>
                </ul>
              </div>

              {/* Show global login error if any */}
              {(loginError || error) && (
                <div className="c-user-login__error">
                  {loginError || error}
                </div>
              )}

              {/* Show loading state during API calls */}
              {(isLoading || formSubmitting) && (
                <div className="c-user-login__loading">
                  <div className="spinner"></div>
                  <p>Loading your game data...</p>
                </div>
              )}

              <div className={`c-user-login__tab-content ${activeTab === 'login' && !isLoading && !formSubmitting ? 'active' : ''}`} id="login-tab">
                <ul className="c-user-login__social">
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('metamask'); }}>
                      <img src={getAssetPath('assets/images/metamask.svg')} alt="Metamask" />
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('google'); }}>
                      <i className="bi bi-google"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('github'); }}>
                      <i className="bi bi-github"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('twitter'); }}>
                      <i className="bi bi-twitter-x"></i>
                    </a>
                  </li>
                </ul>
                <div className="c-user-login__separate">OR</div>
                <form onSubmit={handleSubmit}>
                  <div className="c-input-group">
                    <label htmlFor="login-email" className="c-form-label">E-mail</label>
                    <div className="c-input-floating">
                      <input
                        type="email"
                        className="c-form-control"
                        id="login-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter e-mail address"
                        disabled={formSubmitting}
                        required
                      />
                      <i className="email"></i>
                    </div>
                  </div>
                  <div className="c-input-group">
                    <label htmlFor="login-password" className="c-form-label">
                      Password
                      <a href="#" className="forgot-password" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}>
                        Forgot Password
                      </a>
                    </label>
                    <div className="c-input-floating">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="c-form-control"
                        id="login-password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter password"
                        disabled={formSubmitting}
                        required
                      />
                      <i className="password"></i>
                      <span className="c-input-showpassword" onClick={() => setShowPassword(!showPassword)}>
                        <i className={`bi bi-eye${showPassword ? '-slash' : ''}-fill`}></i>
                      </span>
                    </div>
                  </div>
                  <div className="c-input-group d-flex align-items-center justify-content-between mb-0">
                    <button type="submit" className="c-button" disabled={formSubmitting}>
                      <span>SIGN IN</span>
                    </button>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="rememberMe"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleInputChange}
                        disabled={formSubmitting}
                      />
                      <label className="form-check-label" htmlFor="rememberMe">Remember me</label>
                    </div>
                  </div>
                </form>
              </div>

              <div className={`c-user-login__tab-content ${activeTab === 'register' && !isLoading && !formSubmitting ? 'active' : ''}`} id="register-tab">
                <ul className="c-user-login__social">
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('metamask'); }}>
                      <img src={getAssetPath('assets/images/metamask.svg')} alt="Metamask" />
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('google'); }}>
                      <i className="bi bi-google"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('github'); }}>
                      <i className="bi bi-github"></i>
                    </a>
                  </li>
                  <li>
                    <a href="#" onClick={(e) => { e.preventDefault(); handleSocialLogin('twitter'); }}>
                      <i className="bi bi-twitter-x"></i>
                    </a>
                  </li>
                </ul>
                <div className="c-user-login__separate">OR</div>
                <form onSubmit={handleSubmit}>
                  <div className="c-input-group">
                    <label htmlFor="register-email" className="c-form-label">E-mail</label>
                    <div className="c-input-floating">
                      <input
                        type="email"
                        className="c-form-control"
                        id="register-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter e-mail address"
                        disabled={formSubmitting}
                        required
                      />
                      <i className="email"></i>
                    </div>
                  </div>
                  <div className="c-input-group">
                    <label htmlFor="register-password" className="c-form-label">Create a password</label>
                    <div className="c-input-floating">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="c-form-control"
                        id="register-password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Create a password"
                        disabled={formSubmitting}
                        required
                      />
                      <i className="password"></i>
                      <span className="c-input-showpassword" onClick={() => setShowPassword(!showPassword)}>
                        <i className={`bi bi-eye${showPassword ? '-slash' : ''}-fill`}></i>
                      </span>
                    </div>
                  </div>
                  <div className="c-input-group">
                    <label htmlFor="confirmPassword" className="c-form-label">Confirm password</label>
                    <div className="c-input-floating">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="c-form-control"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm password"
                        disabled={formSubmitting}
                        required
                      />
                      <i className="password"></i>
                      <span className="c-input-showpassword" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        <i className={`bi bi-eye${showConfirmPassword ? '-slash' : ''}-fill`}></i>
                      </span>
                    </div>
                  </div>
                  <p className="c-form-text">
                    By creating an account, you accept the{' '}
                    <a href="#" target="_blank" rel="noopener noreferrer">Terms of Use</a>{' '}
                    and{' '}
                    <a href="#" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                  </p>
                  <div className="c-input-group d-flex align-items-center justify-content-center mb-0">
                    <button type="submit" className="c-button" disabled={formSubmitting}>
                      <span>Create account</span>
                    </button>
                  </div>
                </form>
              </div>

              {showForgotPassword && (
                <div className="c-user-login__forgotpassword">
                  <div className="c-user-login__forgotpassword-content">
                    <div className="c-user-login__forgotpassword-header">
                      <span className="forgotpassword-close" onClick={() => setShowForgotPassword(false)}>
                        <i className="bi bi-arrow-left-short"></i>
                      </span>
                      Forgot Password
                    </div>
                    <div className="c-user-login__forgotpassword-form">
                      {resetSent ? (
                        <div className="c-user-login__success">
                          <h4>Password Reset Email Sent</h4>
                          <p>Check your email for instructions to reset your password.</p>
                          <button className="c-button" onClick={() => setShowForgotPassword(false)}>
                            <span>Back to Login</span>
                          </button>
                        </div>
                      ) : (
                        <form onSubmit={handleForgotPassword}>
                          <div className="c-input-group">
                            <label htmlFor="forgotEmail" className="c-form-label">E-mail</label>
                            <div className="c-input-floating">
                              <input
                                type="email"
                                className="c-form-control"
                                id="forgotEmail"
                                placeholder="Enter e-mail address"
                                value={forgotPasswordEmail}
                                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                disabled={formSubmitting}
                                required
                              />
                              <i className="email"></i>
                            </div>
                          </div>
                          <div className="c-input-group d-flex align-items-center mb-0 mt-2">
                            <button type="submit" className="c-button" disabled={formSubmitting}>
                              <span>Reset password</span>
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 